'use client';

import { HStack, Box } from '@terra/ui-styled-system';
import { IconButton, Tooltip } from '@terra/ui-design-system';
import {
  PowerIcon,
  ShieldEnergyIcon,
  Loading03Icon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
  LayoutLeftIcon,
} from '@terra/ui-icons';
import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { TopBarTrackingToggle } from './TopBarTrackingToggle';
import { TrackingStatusIndicator } from './TrackingStatusIndicator';
import { CrawlerStatusPopover } from './CrawlerStatusPopover';
import { TopBarCredentialsMenu } from './credentials-menu/TopBarCredentialsMenu';
import { TopBarBrowserLauncher } from './TopBarBrowserLauncher';
import { TopBarExternalNetworkScanButton } from './TopBarExternalNetworkScanButton';
import { TopBarInterceptIndicator } from './TopBarInterceptIndicator';
import { GroupedTabBar } from './GroupedTabBar';
import { useProxyStore } from '../../store/proxy-store';
import { useElectronProxy } from '../../hooks/useElectronProxy';
import { useElectronAPI } from '../../hooks/useElectronAPI';
import { useProjectStore } from '../../store/projectStore';
import {
  useMainContentTabs,
  useSidebarMinimized,
} from '../../store/layoutStore';
import {
  isAppTarget,
  TARGET_TYPE,
  targetSupports,
} from '../../utils/target-scope.utils';
import {
  SIDEBAR_WIDTH_EXPANDED,
  TOP_BAR_WINDOW_CONTROLS_RAIL_WIDTH,
} from './sidebarWidth';

const NO_DRAG = { WebkitAppRegion: 'no-drag' } as React.CSSProperties;

export function TopBar() {
  const isProxyRunning = useProxyStore((state) => state.isProxyRunning);
  const isProxyStopping = useProxyStore((state) => state.isProxyStopping);
  const settings = useProxyStore((state) => state.settings);
  const { selectedTarget, targetType } = useProjectStore();
  const { startProxy, stopProxy } = useElectronProxy();
  const { isCACertificateInstalled } = useElectronAPI();
  const {
    openTab,
    goBackInActiveTab,
    goForwardInActiveTab,
    canGoBackInActiveTab,
    canGoForwardInActiveTab,
  } = useMainContentTabs();
  const { isSidebarMinimized, toggleSidebarMinimized } = useSidebarMinimized();
  const t = useTranslations('layout');
  const sidebarToggleLabel = isSidebarMinimized
    ? t('expandSidebar')
    : t('collapseSidebar');
  const leadingRailWidth = isSidebarMinimized
    ? TOP_BAR_WINDOW_CONTROLS_RAIL_WIDTH
    : SIDEBAR_WIDTH_EXPANDED;
  const showCredentialsMenu =
    Boolean(selectedTarget?.id) &&
    targetSupports({ targetType, capability: 'credentials' });

  const [isCertInstalled, setIsCertInstalled] = useState(true);

  useEffect(() => {
    const checkCertificate = async () => {
      try {
        const result = await isCACertificateInstalled();

        setIsCertInstalled(result.success && result.data?.installed === true);
      } catch (error) {
        console.error('Failed to check certificate status:', error);
        setIsCertInstalled(false);
      }
    };

    void checkCertificate();
    const interval = setInterval(() => {
      void checkCertificate();
    }, 10000);

    return () => clearInterval(interval);
  }, [isCACertificateInstalled]);
  const handleStartProxy = async () => {
    await startProxy(settings);
  };

  const handleStopProxy = async () => {
    await stopProxy();
  };

  const handleOpenWelcomeTab = () => {
    openTab({
      id: 'welcome',
      title: t('welcome'),
      type: 'welcome',
    });
  };

  const historyNavButtons = (
    <>
      <Tooltip title={t('back')}>
        <IconButton
          variant="ghost"
          size="xs"
          onClick={goBackInActiveTab}
          disabled={!canGoBackInActiveTab}
          aria-label={t('goBack')}
          style={NO_DRAG}
        >
          <ArrowLeft01Icon size={16} strokeWidth={1.75} />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('forward')}>
        <IconButton
          variant="ghost"
          size="xs"
          onClick={goForwardInActiveTab}
          disabled={!canGoForwardInActiveTab}
          aria-label={t('goForward')}
          style={NO_DRAG}
        >
          <ArrowRight01Icon size={16} strokeWidth={1.75} />
        </IconButton>
      </Tooltip>
    </>
  );

  return (
    <Box
      h="10"
      borderBottomWidth="1px"
      borderColor="border.neutral.subtle"
      pr="3"
      display="flex"
      alignItems="center"
      gap="0"
      bg="bg.light"
      overflow="hidden"
      style={
        {
          WebkitAppRegion: 'drag',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        } as React.CSSProperties & {
          scrollbarWidth?: string;
          msOverflowStyle?: string;
        }
      }
    >
      <HStack
        flexShrink={0}
        gap="0.5"
        pl="20"
        justifyContent="flex-start"
        alignItems="center"
        style={{ width: leadingRailWidth, minWidth: leadingRailWidth }}
      >
        <Tooltip title={sidebarToggleLabel}>
          <IconButton
            variant="ghost"
            size="xs"
            onClick={toggleSidebarMinimized}
            aria-label={sidebarToggleLabel}
            style={NO_DRAG}
          >
            <LayoutLeftIcon size={16} strokeWidth={1.75} />
          </IconButton>
        </Tooltip>
        {historyNavButtons}
      </HStack>

      <Box flex="1" minW="0" h="full" pl="2">
        <GroupedTabBar />
      </Box>

      <Box
        w="1px"
        h="6"
        bg="border.neutral.subtle"
        flexShrink={0}
        style={NO_DRAG}
      />

      <HStack gap="0" flexShrink={0} style={NO_DRAG}>
        {isAppTarget(targetType) ? (
          <>
            {showCredentialsMenu && selectedTarget ? (
              <TopBarCredentialsMenu applicationId={selectedTarget.id} />
            ) : null}
            <CrawlerStatusPopover
              applicationId={selectedTarget?.id}
              disabled={!selectedTarget}
              isBackendOnly={selectedTarget?.isBackendOnly ?? false}
              isDraft={selectedTarget?.isDraft === true}
            />
          </>
        ) : targetType === TARGET_TYPE.EXTERNAL_NETWORK &&
          selectedTarget?.id ? (
          <TopBarExternalNetworkScanButton surfaceId={selectedTarget.id} />
        ) : null}

        <TopBarInterceptIndicator />

        {!isCertInstalled && (
          <Tooltip title={t('certificateNotInstalled')}>
            <IconButton
              variant="ghost"
              size="xs"
              onClick={handleOpenWelcomeTab}
              aria-label={t('installCertificate')}
            >
              <ShieldEnergyIcon size={16} color="orange" />
            </IconButton>
          </Tooltip>
        )}

        <TrackingStatusIndicator />
        <TopBarTrackingToggle />

        <Box data-tour-id="launch-browser" display="inline-flex">
          <TopBarBrowserLauncher />
        </Box>

        <Box data-tour-id="start-proxy" display="inline-flex">
          {isProxyStopping ? (
            <Tooltip title={t('stoppingProxy')}>
              <IconButton
                variant="ghost"
                colorPalette="green"
                size="xs"
                disabled
                aria-label={t('stoppingProxy')}
              >
                <Loading03Icon
                  size={16}
                  strokeWidth={1.75}
                  style={{ animation: 'spin 1s linear infinite' }}
                />
              </IconButton>
            </Tooltip>
          ) : isProxyRunning ? (
            <Tooltip title={t('stopProxy')}>
              <IconButton
                variant="ghost"
                colorPalette="green"
                size="xs"
                onClick={handleStopProxy}
                aria-label={t('stopProxy')}
                color="fg.success"
              >
                <PowerIcon size={16} strokeWidth={1.75} />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title={t('startProxy')}>
              <IconButton
                variant="ghost"
                size="xs"
                onClick={handleStartProxy}
                aria-label={t('startProxy')}
                color="fg.error"
              >
                <PowerIcon size={16} strokeWidth={1.75} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </HStack>
    </Box>
  );
}
